import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { AdminGate, AdminShell } from "./AdminGate";
import { toast } from "sonner";
import { Loader2, ScrollText, Plus, Eye } from "lucide-react";

function Termos({ user }) {
  const [versoes, setVersoes] = useState(null);
  const [aberta, setAberta] = useState(null);
  const [novoTexto, setNovoTexto] = useState("");
  const [criando, setCriando] = useState(false);
  const [busy, setBusy] = useState(false);
  const isSuper = user?.admin_nivel === "super";

  const load = () => api.get("/admin/termos").then((r) => setVersoes(r.data.versoes)).catch(() => setVersoes([]));
  useEffect(() => { load(); }, []);

  const criar = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/termos", { texto: novoTexto });
      toast.success(`Versão ${r.data.versao} criada como rascunho. Publique para exigir novo aceite.`);
      setCriando(false);
      setNovoTexto("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao criar versão");
    } finally {
      setBusy(false);
    }
  };

  const publicar = async (v) => {
    if (!window.confirm(`Publicar a versão ${v.versao}? Todos os motoristas precisarão aceitar novamente.`)) return;
    try {
      await api.post(`/admin/termos/${v.versao}/publicar`);
      toast.success(`Versão ${v.versao} publicada!`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao publicar"); }
  };

  return (
    <AdminShell user={user} title="Termos e Documentos">
      <div className="px-4 mt-4 space-y-3" data-testid="admin-termos-page">
        {isSuper && (
          <button data-testid="termo-novo-btn" onClick={() => setCriando((v) => !v)}
            className="w-full py-3.5 rounded-2xl bg-[#F5B301] text-navy font-heading font-extrabold text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform">
            <Plus className="w-4 h-4" /> {criando ? "CANCELAR" : "NOVA VERSÃO DO TERMO"}
          </button>
        )}

        {criando && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-2.5" data-testid="termo-form">
            <p className="text-xs font-bold text-navy">Texto da nova versão</p>
            <textarea data-testid="termo-texto" rows={10} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-xs leading-relaxed"
              placeholder="Cole aqui o texto completo do termo..." value={novoTexto} onChange={(e) => setNovoTexto(e.target.value)} />
            <p className="text-[10px] text-amber-600">Importante: o texto definitivo deve ser revisado por advogado.</p>
            <button data-testid="termo-salvar" onClick={criar} disabled={busy || novoTexto.trim().length < 200}
              className="w-full py-3 rounded-xl bg-navy text-[#F5B301] font-heading font-extrabold text-sm disabled:opacity-50">
              {busy ? "Salvando..." : "CRIAR RASCUNHO"}
            </button>
          </div>
        )}

        {versoes === null && <div className="flex justify-center py-14"><Loader2 className="w-8 h-8 animate-spin text-[#F5B301]" /></div>}
        {(versoes || []).map((v) => (
          <div key={v.versao} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid={`termo-v${v.versao}`}>
            <div className="flex items-center justify-between mb-1">
              <p className="font-heading font-bold text-navy text-sm flex items-center gap-1.5">
                <ScrollText className="w-4 h-4 text-[#F5B301]" /> Versão {v.versao}
              </p>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${v.ativo ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid={`termo-status-${v.versao}`}>
                {v.ativo ? "VIGENTE" : v.publicado_em ? "SUBSTITUÍDA" : "RASCUNHO"}
              </span>
            </div>
            <p className="text-[11px] text-slate-400">
              hash {v.hash} · {v.total_aceites} aceite(s){v.publicado_em ? ` · publicada em ${new Date(v.publicado_em).toLocaleString("pt-BR")}` : ""}
            </p>
            <div className="flex gap-1.5 mt-2.5 flex-wrap">
              <button data-testid={`termo-ver-${v.versao}`} onClick={() => setAberta(aberta?.versao === v.versao ? null : v)}
                className="px-3 py-2 rounded-lg bg-slate-100 text-slate-600 text-[10px] font-bold flex items-center gap-1">
                <Eye className="w-3 h-3" /> {aberta?.versao === v.versao ? "FECHAR" : "VER TEXTO"}
              </button>
              {isSuper && !v.ativo && (
                <button data-testid={`termo-publicar-${v.versao}`} onClick={() => publicar(v)}
                  className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-[10px] font-bold">PUBLICAR</button>
              )}
            </div>
            {aberta?.versao === v.versao && (
              <div className="mt-3 border-t border-slate-100 pt-3">
                <pre className="text-[11px] text-slate-600 whitespace-pre-wrap leading-relaxed max-h-72 overflow-y-auto bg-slate-50 rounded-xl p-3" data-testid={`termo-texto-view-${v.versao}`}>
                  {v.texto}
                </pre>
                {v.aceites?.length > 0 && (
                  <div className="mt-2.5" data-testid={`termo-aceites-${v.versao}`}>
                    <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Quem aceitou</p>
                    {v.aceites.map((a) => (
                      <p key={a.id} className="text-[11px] text-slate-500">
                        {a.nome} · {new Date(a.em).toLocaleString("pt-BR")}{a.ip ? ` · IP ${a.ip}` : ""}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </AdminShell>
  );
}

export default function AdminTerms() {
  return <AdminGate>{(user) => <Termos user={user} />}</AdminGate>;
}
